import {
    Box,
    Button,
    Flex,
    Grid,
    GridItem,
    HStack,
    Heading,
    IconButton,
    Link,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    Tooltip,
    useColorModeValue
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { FaPlugCircleCheck, FaPlugCircleXmark } from "react-icons/fa6";

import { AppMenuButtons } from "../parts/AppMenuButtons";
import { DrumScope, MISSED_COLOR } from "../parts/DrumScope";
import {
    CMD_APPLY,
    CMD_RESET,
    CMD_SAVE,
    CONFIG_REPORT_ID,
    FULL_SCALE_MAX,
    FULL_SCALE_MIN,
    GAIN_UI_MAX,
    GAIN_UI_MIN,
    NOISE_FLOOR_MAX,
    RELEASE_MIN,
    TRIGGER_MIN,
    clampBoardConfig,
    decodeBoardConfig,
    defaultBoardConfig,
    encodeBoardConfig,
    gainToMultiplier,
    multiplierToGain,
    playerIsLongTail
} from "../lib/taikoControllerConfig";
import { useDocumentTitle } from "../parts/useDocumentTitle";

import { Helmet } from "react-helmet";

import {
    CHANNELS_PER_PLAYER,
    CHANNEL_COUNT,
    DEFAULT_POLL_RATE_HZ,
    DON,
    MAX_REACHABLE_PERCENT,
    POLL_RATE_CHOICES,
    ZONE_LEFT_DON,
    ZONE_LEFT_KA,
    ZONE_RIGHT_DON,
    ZONE_RIGHT_KA,
    channelGroupKey,
    channelSurface,
    channelZone,
    createEmulator,
    decodeArcadeAxes,
    defaultThresholds,
    sanitizeThresholds,
    withThresholdEdit
} from "../lib/taikoGameEmulation";

import bgimg_ld from "../images/taiko/ld.png";
import bgimg_rd from "../images/taiko/rd.png";
import bgimg_lk from "../images/taiko/lk.png";
import bgimg_rk from "../images/taiko/rk.png";

import bgvid from "../videos/taiko_test.mp4";
import bgimg from "../images/taiko/poster.webp";

const COUNTER_REFRESH_HZ = 5;
// Drawing is decoupled from sampling. The emulator consumes every report on
// every animation frame so the game-side timing stays exact; only the repaint
// is throttled, which is purely cosmetic and cheap to give up.
const RENDER_FPS = 30;
const RENDER_INTERVAL_MS = 1000 / RENDER_FPS;
// A backgrounded tab stops running animation frames while the controller keeps
// reporting at 1 kHz, so cap the backlog rather than buffering it forever.
const MAX_QUEUED_REPORTS = 4096;

const THRESHOLDS_STORAGE_KEY = "taikoThresholds";
const POLL_RATE_STORAGE_KEY = "taikoPollRate";

// Arcade mode HID identity and report layout of the ESP32-S3 firmware: the
// report body is nine bytes with signed 8-bit X/Y (P1) and Z/Rz (P2) axes,
// hat, and a 32-bit button bitmap.
const TAIKO_VENDOR_ID = 0x4869;
const TAIKO_PRODUCT_ID = 0x4869;
const TAIKO_REPORT_ID = 0x01;
const TAIKO_REPORT_MIN_BYTES = 4;

const DON_COLOR = "#f64824";
const KA_COLOR = "#65beba";
const SOFT_COLOR = "#9d9d9d";

const ZONE_INFO = {
    [ZONE_LEFT_DON]: { label: "Left Don", kana: "左面", image: bgimg_ld },
    [ZONE_RIGHT_DON]: { label: "Right Don", kana: "右面", image: bgimg_rd },
    [ZONE_LEFT_KA]: { label: "Left Ka", kana: "左ふち", image: bgimg_lk },
    [ZONE_RIGHT_KA]: { label: "Right Ka", kana: "右ふち", image: bgimg_rk },
};

const emptyTallies = () =>
    Array.from({ length: CHANNEL_COUNT },
        () => ({ light: 0, hard: 0, missed: 0, soft: 0, holdMs: 0 }));

const isTaikoDevice = (device) =>
    device.vendorId === TAIKO_VENDOR_ID &&
    device.productId === TAIKO_PRODUCT_ID;

const readStoredThresholds = () => {
    try {
        return sanitizeThresholds(JSON.parse(localStorage.getItem(THRESHOLDS_STORAGE_KEY)));
    } catch (error) {
        return defaultThresholds();
    }
};

const readStoredPollRate = () => {
    const stored = Number(localStorage.getItem(POLL_RATE_STORAGE_KEY));
    return POLL_RATE_CHOICES.includes(stored) ? stored : DEFAULT_POLL_RATE_HZ;
};

// The three outcomes drawn on the waveform, reused for the per zone tally so
// the counts and the markers cannot drift apart.
const HitGlyph = ({ variant, color, filled }) => {
    if (variant === "missed") {
        return <Box w="11px" h="11px" borderRadius="full" borderWidth="2px" borderStyle="dashed" borderColor={MISSED_COLOR} />;
    }
    if (variant === "soft") {
        return <Box w="8px" h="8px" borderRadius="full" borderWidth="1px" borderColor={SOFT_COLOR} />;
    }
    const size = variant === "hard" ? "13px" : "8px";
    return (
        <Box
            w={size}
            h={size}
            borderRadius="full"
            bg={filled ? color : "transparent"}
            borderWidth={filled ? "0" : "2px"}
            borderColor={color}
        />
    );
};

const HitTally = ({ counts, color, filled }) => (
    <HStack spacing="10px" fontSize="xs" color="gray.500">
        <Tooltip label="How long the controller held the axis for the last hit, measured from the report stream. The game needs this to outlast one of its frames.">
            <Text>{counts.holdMs > 0 ? `${counts.holdMs.toFixed(1)} ms` : "\u2013"}</Text>
        </Tooltip>
        <Tooltip label="Registered as a small note">
            <HStack spacing="4px"><HitGlyph variant="light" color={color} filled={filled} /><Text>{counts.light}</Text></HStack>
        </Tooltip>
        <Tooltip label="Registered at or above the big note threshold">
            <HStack spacing="4px"><HitGlyph variant="hard" color={color} filled={filled} /><Text>{counts.hard}</Text></HStack>
        </Tooltip>
        <Tooltip label="Loud enough to register, but no game frame sampled the hold">
            <HStack spacing="4px"><HitGlyph variant="missed" /><Text>{counts.missed}</Text></HStack>
        </Tooltip>
        <Tooltip label="Below the small note threshold, so the game ignores it">
            <HStack spacing="4px"><HitGlyph variant="soft" /><Text>{counts.soft}</Text></HStack>
        </Tooltip>
    </HStack>
);

const ThresholdRow = ({ label, tooltip, value, min, max, onChange, step = 1, precision = 0 }) => (
    <Flex align="center" gap="8px">
        <Tooltip label={tooltip}>
            <Text fontSize="xs" color="gray.500" w="48px" flexShrink={0}>{label}</Text>
        </Tooltip>
        <Slider
            flex="1"
            minW="0"
            value={value}
            min={min}
            max={max}
            step={step}
            focusThumbOnChange={false}
            onChange={onChange}
        >
            <SliderTrack><SliderFilledTrack /></SliderTrack>
            <SliderThumb />
        </Slider>
        <NumberInput
            size="xs"
            maxW="72px"
            flexShrink={0}
            value={precision > 0 ? value.toFixed(precision) : value}
            min={min}
            max={max}
            step={step}
            precision={precision}
            onChange={(_, numeric) => Number.isFinite(numeric) && onChange(numeric)}
        >
            <NumberInputField />
            <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
            </NumberInputStepper>
        </NumberInput>
    </Flex>
);

// The board's own detector levels, one set per player. These are what the
// firmware compares the envelope against before it ever reaches the game.
const BoardTuningBar = ({ tuning, longTail, onEdit }) => (
    <Flex direction="column" gap="8px" p="12px" borderWidth="1px" borderRadius="md">
        <Flex align="center" gap="10px" wrap="wrap">
            <Text fontSize="xs" fontWeight="bold" color="gray.500">BOARD DETECTOR</Text>
            <Tooltip label="Selected by the DIP switch, from the shape of the drum's waveform">
                <Text fontSize="xs" color="gray.500">
                    {longTail ? "long-tail drum" : "standard drum"}
                </Text>
            </Tooltip>
        </Flex>
        <Grid templateColumns={["1fr", "1fr", "1fr 1fr"]} gap="8px 18px">
            <ThresholdRow
                label="Trigger"
                tooltip="Envelope level a strike must reach for the detector to consider it a hit"
                value={tuning.trigger}
                min={TRIGGER_MIN}
                max={tuning.fullScale - 1}
                onChange={(value) => onEdit("trigger", value)}
            />
            <ThresholdRow
                label="Release"
                tooltip="Level the zone must fall below before it can arm again"
                value={tuning.release}
                min={RELEASE_MIN}
                max={tuning.trigger - 1}
                onChange={(value) => onEdit("release", value)}
            />
            <ThresholdRow
                label="Noise"
                tooltip="Subtracted from every sample before the envelope is built"
                value={tuning.noiseFloor}
                min={0}
                max={NOISE_FLOOR_MAX}
                onChange={(value) => onEdit("noiseFloor", value)}
            />
            <ThresholdRow
                label="Range"
                tooltip="Envelope level that maps to a full strength axis value, so it sets how hard a hit must be to reach 50% in game"
                value={tuning.fullScale}
                min={FULL_SCALE_MIN}
                max={FULL_SCALE_MAX}
                onChange={(value) => onEdit("fullScale", value)}
            />
        </Grid>
    </Flex>
);

const App = () => {

    useDocumentTitle("Taiko Controller Test");

    const [controllerConnected, setControllerConnected] = useState(false);
    const [webHidSupported] = useState(() => typeof navigator !== "undefined" && "hid" in navigator);
    const [thresholds, setThresholds] = useState(readStoredThresholds);
    const [pollRate, setPollRate] = useState(readStoredPollRate);
    const [tallies, setTallies] = useState(emptyTallies);
    const [reportRateHz, setReportRateHz] = useState(0);

    // null until the board answers a tuning read; firmware without the channel
    // stays null and the board controls stay hidden.
    const [boardConfig, setBoardConfig] = useState(null);
    const [boardBusy, setBoardBusy] = useState(false);
    const [boardNotice, setBoardNotice] = useState("Connect the controller to read its tuning.");

    const deviceRef = useRef(null);
    // Mirrors boardConfig so an edit can compute the next value without doing
    // work inside a state updater, which React may invoke more than once.
    const boardConfigRef = useRef(null);
    const boardWriteTimerRef = useRef(null);
    const reportQueueRef = useRef([]);
    const scopeRefs = useRef([]);

    const emulatorRef = useRef(null);
    if (emulatorRef.current === null) {
        emulatorRef.current = createEmulator({ pollRateHz: pollRate });
        emulatorRef.current.setThresholds(thresholds);
    }

    // DrumScope reads its thresholds through a ref so a slider drag never
    // rebuilds the canvas draw callbacks.
    const thresholdRefs = useRef(null);
    if (thresholdRefs.current === null) {
        thresholdRefs.current = Array.from({ length: CHANNEL_COUNT },
            (_, channel) => ({ current: thresholds[channelGroupKey(channel)] }));
    }

    const gridColor = useColorModeValue("#00000044", "#ffffff44");
    const labelColor = useColorModeValue("#00000066", "#ffffff66");
    const surfaceColor = useColorModeValue("#ffffffdd", "#1a202cdd");

    // Stable per channel, so a threshold drag re-rendering the page cannot
    // detach and reattach every scope between animation frames.
    const scopeRefCallbacks = useRef(null);
    if (scopeRefCallbacks.current === null) {
        scopeRefCallbacks.current = Array.from({ length: CHANNEL_COUNT },
            (_, channel) => (node) => { scopeRefs.current[channel] = node; });
    }

    useEffect(() => {
        for (let channel = 0; channel < CHANNEL_COUNT; channel++) {
            thresholdRefs.current[channel].current = thresholds[channelGroupKey(channel)];
        }
        emulatorRef.current.setThresholds(thresholds);
        localStorage.setItem(THRESHOLDS_STORAGE_KEY, JSON.stringify(thresholds));
    }, [thresholds]);

    useEffect(() => {
        emulatorRef.current.setPollRate(pollRate);
        localStorage.setItem(POLL_RATE_STORAGE_KEY, String(pollRate));
    }, [pollRate]);

    const handleInputReport = useCallback((event) => {
        if (event.reportId !== TAIKO_REPORT_ID ||
            event.data.byteLength < TAIKO_REPORT_MIN_BYTES) {
            return;
        }
        const queue = reportQueueRef.current;
        if (queue.length >= MAX_QUEUED_REPORTS) {
            queue.length = 0;
        }
        // No timestamp: the emulator counts reports instead, because the
        // moment this handler runs says nothing about when the drum was hit.
        queue.push(decodeArcadeAxes(event.data, new Int8Array(CHANNEL_COUNT)));
    }, []);

    // Every animation frame drains the report queue, so no sample is ever
    // skipped; repainting runs on its own slower budget.
    useEffect(() => {
        let lastDrawMs = 0;
        let frame = requestAnimationFrame(function tick(nowMs) {
            emulatorRef.current.advance(reportQueueRef.current, nowMs);
            if (nowMs - lastDrawMs >= RENDER_INTERVAL_MS) {
                lastDrawMs = nowMs;
                scopeRefs.current.forEach((scope) => scope && scope.draw());
            }
            frame = requestAnimationFrame(tick);
        });
        return () => cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTallies(emulatorRef.current.channels.map(
                ({ counts, lastHoldMs }) => ({ ...counts, holdMs: lastHoldMs })));
            setReportRateHz(controllerConnected ? emulatorRef.current.reportRateHz : 0);
        }, 1000 / COUNTER_REFRESH_HZ);
        return () => clearInterval(intervalId);
    }, [controllerConnected]);

    useEffect(() => () => {
        if (boardWriteTimerRef.current) {
            clearTimeout(boardWriteTimerRef.current);
        }
    }, []);

    const readBoardConfig = useCallback(async (device) => {
        try {
            const view = await device.receiveFeatureReport(CONFIG_REPORT_ID);
            const config = decodeBoardConfig(view);
            boardConfigRef.current = config;
            setBoardConfig(config);
            setBoardNotice(config
                ? null
                : "The controller answered, but not with a tuning report this page recognises.");
        } catch (error) {
            console.info("Taiko Controller did not answer the tuning channel.", error);
            boardConfigRef.current = null;
            setBoardConfig(null);
            setBoardNotice("This controller did not answer the tuning channel. It needs Arcade mode and firmware built with the HID tuning report.");
        }
    }, []);

    const sendBoardConfig = useCallback(async (config, command) => {
        const device = deviceRef.current;
        if (!device || !config) {
            return;
        }
        try {
            await device.sendFeatureReport(
                CONFIG_REPORT_ID, encodeBoardConfig(config, command));
        } catch (error) {
            console.warn("Failed to write Taiko Controller tuning.", error);
        }
    }, []);

    // Slider drags would otherwise put a control transfer on the wire per
    // pixel; the board only needs the value the drag settles on.
    const scheduleBoardWrite = useCallback((config) => {
        if (boardWriteTimerRef.current) {
            clearTimeout(boardWriteTimerRef.current);
        }
        boardWriteTimerRef.current = setTimeout(() => {
            boardWriteTimerRef.current = null;
            sendBoardConfig(config, CMD_APPLY);
        }, 80);
    }, [sendBoardConfig]);

    const editBoardConfig = useCallback((mutate) => {
        const current = boardConfigRef.current;
        if (!current) {
            return;
        }
        // clampBoardConfig already rebuilds every player and gain array, so the
        // mutation below cannot reach the state object we were handed.
        const next = clampBoardConfig(current);
        mutate(next);
        const clamped = clampBoardConfig(next);
        boardConfigRef.current = clamped;
        setBoardConfig(clamped);
        scheduleBoardWrite(clamped);
    }, [scheduleBoardWrite]);

    const commitBoardConfig = useCallback(async (command) => {
        setBoardBusy(true);
        const config = command === CMD_RESET ? defaultBoardConfig() : boardConfig;
        await sendBoardConfig(config, command);
        const device = deviceRef.current;
        if (device) {
            await readBoardConfig(device);
        }
        setBoardBusy(false);
    }, [boardConfig, sendBoardConfig, readBoardConfig]);

    const attachDevice = useCallback(async (device) => {
        if (deviceRef.current) {
            return;
        }
        try {
            if (!device.opened) {
                await device.open();
            }
        } catch (error) {
            console.warn("Failed to open Taiko Controller.", error);
            return;
        }
        device.oninputreport = handleInputReport;
        deviceRef.current = device;
        setControllerConnected(true);
        readBoardConfig(device);
        console.log("Taiko Controller connected.");
    }, [handleInputReport, readBoardConfig]);

    const detachDevice = useCallback((device) => {
        if (deviceRef.current !== device) {
            return;
        }
        device.oninputreport = null;
        deviceRef.current = null;
        setControllerConnected(false);
        boardConfigRef.current = null;
        setBoardConfig(null);
        setBoardNotice("Connect the controller to read its tuning.");
        console.log("Taiko Controller disconnected.");
    }, []);

    const requestConnection = useCallback(async () => {
        if (!webHidSupported || deviceRef.current) {
            return;
        }
        try {
            const [device] = await navigator.hid.requestDevice({
                filters: [{ vendorId: TAIKO_VENDOR_ID, productId: TAIKO_PRODUCT_ID }],
            });
            if (device) {
                await attachDevice(device);
            }
        } catch (error) {
            console.warn("Taiko Controller request canceled or failed.", error);
        }
    }, [webHidSupported, attachDevice]);

    useEffect(() => {

        if (!webHidSupported) {
            return undefined;
        }

        let cancelled = false;

        const handleConnect = (e) => {
            if (isTaikoDevice(e.device)) {
                attachDevice(e.device);
            }
        };

        const handleDisconnect = (e) => {
            if (isTaikoDevice(e.device)) {
                detachDevice(e.device);
            }
        };

        navigator.hid.addEventListener("connect", handleConnect);
        navigator.hid.addEventListener("disconnect", handleDisconnect);
        // Reopen a previously granted device without another permission
        // prompt, so revisits and replugs reconnect automatically.
        navigator.hid.getDevices().then((devices) => {
            const device = devices.find(isTaikoDevice);
            if (!cancelled && device) {
                attachDevice(device);
            }
        });

        return () => {
            cancelled = true;
            navigator.hid.removeEventListener("connect", handleConnect);
            navigator.hid.removeEventListener("disconnect", handleDisconnect);
            const device = deviceRef.current;
            if (device) {
                device.oninputreport = null;
                device.close().catch(() => { });
            }
            deviceRef.current = null;
        };
    }, [webHidSupported, attachDevice, detachDevice]);

    const editThreshold = useCallback((groupKey, field, value) => {
        setThresholds((current) => withThresholdEdit(current, groupKey, field, value));
    }, []);

    const resetCounts = useCallback(() => {
        emulatorRef.current.resetCounts();
        setTallies(emptyTallies());
    }, []);

    const channels = emulatorRef.current.channels;
    const getWriteIndex = useCallback(() => emulatorRef.current.writeIndex, []);

    const renderPlayer = (player) => (
        <Flex p="24px" boxShadow="lg" borderWidth="1px" borderRadius="lg" direction="column" rowGap="18px">
            <Heading fontSize={28}>{`Player ${player + 1} Drum`}</Heading>
            {boardConfig ? (
                <BoardTuningBar
                    tuning={boardConfig.players[player]}
                    longTail={playerIsLongTail(boardConfig.status, player)}
                    onEdit={(field, value) =>
                        editBoardConfig((next) => { next.players[player][field] = value; })}
                />
            ) : (
                <Flex direction="column" gap="6px" p="12px" borderWidth="1px" borderRadius="md">
                    <Text fontSize="xs" fontWeight="bold" color="gray.500">BOARD DETECTOR</Text>
                    <Text fontSize="xs" color="gray.500">{boardNotice}</Text>
                </Flex>
            )}
            <Grid templateColumns={["1fr", "1fr", "1fr 1fr", "1fr 1fr 1fr 1fr"]} gap="18px">
                {[ZONE_LEFT_DON, ZONE_RIGHT_DON, ZONE_LEFT_KA, ZONE_RIGHT_KA].map((zone) => {
                    const channel = player * CHANNELS_PER_PLAYER + zone;
                    const surface = channelSurface(channel);
                    const groupKey = channelGroupKey(channel);
                    const color = surface === DON ? DON_COLOR : KA_COLOR;
                    const info = ZONE_INFO[channelZone(channel)];
                    const sharedWith = surface === DON ? "both Don pads" : "both Ka rims";
                    return (
                        <GridItem key={channel}>
                            <Flex direction="column" gap="10px">
                                <Flex align="center" justify="space-between" gap="8px">
                                    <Tooltip
                                        label={`Dotted lines are the TAIKO TEST ${player + 1}P ${surface === DON ? "面" : "ふち"} thresholds, shared by ${sharedWith}. Drag them to change.`}
                                    >
                                        <Text fontSize="sm" fontWeight="bold">
                                            {info.label}
                                            <Text as="span" color="gray.500" fontWeight="normal">{` ${info.kana}`}</Text>
                                        </Text>
                                    </Tooltip>
                                    <HitTally counts={tallies[channel]} color={color} filled={surface === DON} />
                                </Flex>
                                <DrumScope
                                    ref={scopeRefCallbacks.current[channel]}
                                    channel={channels[channel]}
                                    getWriteIndex={getWriteIndex}
                                    color={color}
                                    surface={surface}
                                    bgImage={info.image}
                                    gridColor={gridColor}
                                    labelColor={labelColor}
                                    surfaceColor={surfaceColor}
                                    thresholdsRef={thresholdRefs.current[channel]}
                                    onThresholdChange={(field, value) =>
                                        editThreshold(groupKey, field, value)}
                                />
                                {boardConfig && (
                                    <ThresholdRow
                                        label="Gain"
                                        tooltip="Board-side trim for this sensor, before any game threshold applies"
                                        value={gainToMultiplier(boardConfig.players[player].gains[zone])}
                                        min={GAIN_UI_MIN}
                                        max={GAIN_UI_MAX}
                                        step={0.05}
                                        precision={2}
                                        onChange={(value) =>
                                            editBoardConfig((next) => {
                                                next.players[player].gains[zone] =
                                                    multiplierToGain(value);
                                            })}
                                    />
                                )}
                            </Flex>
                        </GridItem>
                    );
                })}
            </Grid>
        </Flex>
    );

    // Doubles as the connection indicator and the button that opens the
    // browser's device chooser.
    const connectionLabel = !webHidSupported
        ? "WebHID unsupported"
        : controllerConnected
            ? "Taiko Controller connected"
            : "Taiko Controller disconnected, click to connect";

    const statusComponent = (
        <IconButton
            size="md"
            fontSize="lg"
            aria-label={connectionLabel}
            title={connectionLabel}
            colorScheme={!webHidSupported ? "orange" : controllerConnected ? "green" : "red"}
            isDisabled={!webHidSupported}
            onClick={webHidSupported ? requestConnection : undefined}
            icon={controllerConnected ? <FaPlugCircleCheck /> : <FaPlugCircleXmark />}
        />
    );

    return (
        <Flex mt="72px" ml="18px" mr="18px" mb="18px" direction="column" gap="24px">
            <Helmet>
                <meta name="description" content={"A Taiko Controller sensitivity adjustment tool."} />
            </Helmet>
            <Flex
                p="18px 24px"
                boxShadow="lg"
                borderWidth="1px"
                borderRadius="lg"
                align="center"
                gap="18px"
                wrap="wrap"
            >
                <Tooltip label="How often the game reads the drum. TaikoArcadeLoader polls once per presented frame, so this is config.toml's fpslimit.">
                    <Text fontSize="sm" color="gray.500">Game poll rate</Text>
                </Tooltip>
                <Select
                    size="sm"
                    w="120px"
                    value={pollRate}
                    onChange={(e) => setPollRate(Number(e.target.value))}
                >
                    {POLL_RATE_CHOICES.map((hz) => (
                        <option key={hz} value={hz}>{`${hz} Hz`}</option>
                    ))}
                </Select>
                <Button size="sm" variant="outline" onClick={() => setThresholds(defaultThresholds())}>
                    Default thresholds
                </Button>
                <Button size="sm" variant="outline" onClick={resetCounts}>
                    Reset counts
                </Button>
                <Tooltip label="Ask the controller for the tuning it is running right now">
                    <Button size="sm" variant="outline" isLoading={boardBusy}
                        isDisabled={!controllerConnected}
                        onClick={() => {
                            const device = deviceRef.current;
                            if (device) {
                                readBoardConfig(device);
                            }
                        }}>
                        Read from board
                    </Button>
                </Tooltip>
                <Tooltip label="Write the current board tuning to the controller's flash so it survives a power cycle">
                    <Button size="sm" variant="outline" isLoading={boardBusy}
                        isDisabled={!boardConfig}
                        onClick={() => commitBoardConfig(CMD_SAVE)}>
                        Save to board
                    </Button>
                </Tooltip>
                <Tooltip label="Restore the board's shipping tuning and forget the stored one">
                    <Button size="sm" variant="outline" isLoading={boardBusy}
                        isDisabled={!boardConfig}
                        onClick={() => commitBoardConfig(CMD_RESET)}>
                        Reset board
                    </Button>
                </Tooltip>
                <Tooltip label="Reports per second actually arriving from the controller, measured rather than assumed. The firmware aims for 1000, but drops a report whenever the USB endpoint is still busy.">
                    <Text fontSize="xs" color="gray.500">
                        {reportRateHz > 0 ? `Device ${Math.round(reportRateHz)} rep/s` : "Device idle"}
                    </Text>
                </Tooltip>
                <Text fontSize="xs" color="gray.500">
                    Drag the dotted lines in a graph to set the game's thresholds.
                </Text>
                <Text fontSize="xs" color="gray.500">
                    {`Full strength on this controller reads as ${MAX_REACHABLE_PERCENT.toFixed(0)}% to the game.`}
                </Text>
            </Flex>
            {renderPlayer(0)}
            {renderPlayer(1)}
            <AppMenuButtons pageLabel="Taiko" appVersion="0.7" modalHeader="About Taiko Controller Tester" customComponent={statusComponent}>
                <p>This page visualizes and tunes the sensitivity of the <Link href="https://github.com/ShikyC/Taiko-Drum-Controller-Arduino" isExternal>Taiko Controller (ESP32-S3 version)</Link>, and reproduces the way Taiko no Tatsujin Nijiiro plus <Link href="https://github.com/ShikyC/TaikoArcadeLoader" isExternal>TaikoArcadeLoader</Link> would read it.</p>
                <br />
                <p>To use this tool, set the DIP switches to Arcade mode (DIP3 and DIP4 OFF), flash the ESP32-S3 firmware, then connect Taiko Controller to the computer and click the plug button in the top right corner to select the device.</p>
                <br />
                <p>There are two layers of tuning here. <b>Board detector</b> settings and the per-zone <b>Gain</b> live on the controller itself and are written to it live over the HID tuning channel; they decide what the firmware treats as a hit and how hard it reports it. Use <i>Save to board</i> to keep them across a power cycle, since edits are otherwise lost on unplug. The drum type beside them is the DIP switch's job and is shown read-only.</p>
                <br />
                <p>The two dotted lines across each waveform are the TAIKO TEST menu's 面 / ふち sensitivity rows, 1 to 100, defaulting to 5 and 25 like the arcade. Drag either line to change it. As in the game there is one pair per player per surface, so the left and right pads always move together, and the small value can never reach the big one.</p>
                <br />
                <p>A hit that clears the small threshold draws a circle on the waveform: filled red for don, hollow blue for ka, small for a normal hit and large for one that reaches the big note threshold.</p>
                <br />
                <p>An amber dashed ring means the hit was loud enough to register but landed between two game frames, so the game never sampled it. The figure beside each zone's counters is the measured hold: how long the controller actually drove that axis, counted from the report stream. It has to outlast one game frame — 16.7 ms at 60 fps — or hits start falling through the gaps.</p>
                <br />
                <video autoPlay muted loop playsInline poster={bgimg}>
                    <source src={bgvid} type="video/mp4" />
                </video>
            </AppMenuButtons>
        </Flex>
    );
};

export default App;
